import React, { useState, useEffect, useMemo } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Star, Filter, Beaker, Mountain, Microscope, Atom, Database, Cpu } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useSupabaseStore } from '@/store/SupabaseStore';

const AgentCard = ({ 
  id,
  name, 
  description, 
  category, 
  rating, 
  uses, 
  icon: Icon,
  standard = false 
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const handleUseAgent = () => {
    navigate(`/agents/use/${id}`);
    toast({
      title: `${name} selected`,
      description: "Opening agent workspace...",
    });
  };
  
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      <CardHeader className="pb-4">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-md text-primary">
              <Icon size={24} />
            </div>
            <CardTitle className="text-lg">{name}</CardTitle>
          </div>
          {standard && (
            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-yellow-300">
              Standard
            </Badge>
          )}
        </div>
        <CardDescription className="line-clamp-2">{description}</CardDescription>
      </CardHeader>
      <CardContent className="pb-4">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Star size={14} className="fill-yellow-400 text-yellow-400" />
            <span>{rating}/5</span>
          </div>
          <div>
            <span>{uses.toLocaleString()} users</span>
          </div>
          <Badge variant="secondary" className="capitalize">
            {category}
          </Badge>
        </div>
      </CardContent>
      <CardFooter className="border-t pt-4 flex gap-2">
        <Button 
          variant="default" 
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"

          onClick={handleUseAgent}
        >
          Use Agent
        </Button>
      </CardFooter>
    </Card>
  );
};

const ScientificAgents = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();
  const { agents, fetchAgents } = useSupabaseStore();

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const scientificCategories = ["physics", "chemistry", "data science", "earth science", "biology", "artificial intelligence"];
  const scientificAgents = useMemo(() => 
    agents.filter(a => a.is_standard && scientificCategories.includes(a.domain)),
    [agents]
  );

  const iconMap = {
    Atom, Beaker, Database, Mountain, Cpu, Microscope
  };

  // Filter agents based on search term
  const filteredAgents = scientificAgents.filter(agent => 
    agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <MainLayout>
      <div className="container px-4 py-6 max-w-7xl mx-auto">
        <div className="flex flex-col space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Scientific AI Agents
              </h1>
              <p className="text-muted-foreground mt-1">
                Cutting-edge AI agents for research, data analysis, and scientific exploration
              </p>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative w-full md:w-96">
              <Input
                placeholder="Search scientific agents..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
            </div>
            
            <div className="flex gap-2 ml-auto">
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2"
                onClick={() => {
                  toast({
                    title: "Filters",
                    description: "Filter functionality coming soon!",
                  });
                }}
              >
                <Filter size={16} />
                Filters
              </Button>
            </div>
          </div>
          
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="physics">Physics</TabsTrigger>
              <TabsTrigger value="chemistry">Chemistry</TabsTrigger>
              <TabsTrigger value="data science">Data Science</TabsTrigger>
              <TabsTrigger value="earth science">Earth Sciences</TabsTrigger>
              <TabsTrigger value="biology">Biology</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAgents.map((agent) => (
                  <AgentCard key={agent.id} {...agent} category={agent.domain} icon={iconMap[agent.icon]} standard={agent.is_standard} />
                ))}
              </div>
            </TabsContent>
            
            {scientificCategories.map((category) => (
              <TabsContent key={category} value={category} className="mt-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredAgents
                    .filter(agent => agent.domain === category)
                    .map((agent) => (
                      <AgentCard key={agent.id} {...agent} category={agent.domain} icon={iconMap[agent.icon]} standard={agent.is_standard} />
                    ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </MainLayout>
  );
};

export default ScientificAgents;